/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-unescaped-entities */
"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Mail, CheckCircle2, BadgeCheck } from "lucide-react";

interface BecomeAskLocalPartnerFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BecomeAskLocalPartnerForm({
  isOpen,
  onClose,
}: BecomeAskLocalPartnerFormProps) {
  const [step, setStep] = useState<"form" | "verify">("form");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mlsLicense, setMlsLicense] = useState("");
  const [email, setEmail] = useState("");

  const [focused, setFocused] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(false);

  const [code, setCode] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(30);

  const API = process.env.NEXT_PUBLIC_API_URL || "";

  useEffect(() => {
    if (!isOpen) return;

    if (step === "verify" && timer > 0) {
      const interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [step, timer, isOpen]);

  const resetForm = () => {
    setStep("form");
    setFirstName("");
    setLastName("");
    setMlsLicense("");
    setEmail("");
    setCode(Array(6).fill(""));
    setTimer(30);
    setFocused(null);
  };

  const safeFetch = async (url: string, options: RequestInit) => {
    if (!API) {
      throw new Error("API URL missing");
    }

    const res = await fetch(url, options);

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`API ${res.status}: ${text || "Request failed"}`);
    }

    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error("Invalid JSON response");
    }

    return res.json();
  };

  // SUBMIT FORM → Next directly to VERIFY
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (!firstName || !lastName || !email || !mlsLicense) {
      alert("Please fill all fields");
      setLoading(false);
      return;
    }

    try {
      await safeFetch(`${API}/api/v1/client-experience-preview/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          mlsLicense,
          email,
        }),
      });

      setStep("verify");
      setTimer(30);
    } catch (err: any) {
      console.error("FORM SUBMIT ERROR:", err);
      alert(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);

    try {
      const data = await safeFetch(
        `${API}/api/v1/client-experience-preview/verify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            code: code.join(""),
          }),
        },
      );

      if (data.success) {
        setPopup(true);
        setTimeout(() => {
          setPopup(false);
          resetForm();
          onClose();
        }, 2000);
      } else {
        alert(data.message || "Invalid code");
      }
    } catch (err: any) {
      console.error("VERIFY ERROR:", err);
      alert(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    const newArr = [...code];
    newArr[index] = value;
    setCode(newArr);

    if (value && index < 5) {
      const next = document.getElementById(`code-${index + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;

    setLoading(true);
    setCode(Array(6).fill(""));
    setTimer(30);

    try {
      await safeFetch(`${API}/api/v1/client-experience-preview/resend-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch (err: any) {
      console.error("RESEND ERROR:", err);
      alert(err.message || "Failed to resend code");
      setTimer(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>

    
      {/* SUCCESS POPUP */}
      {popup && (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          className="fixed top-5 right-5 z-9999"
        >
          <div className="bg-white rounded-xl shadow-lg border px-6 py-4 w-72">
            <h3 className="text-lg font-semibold text-[#4177BC]">
              You're All Set!
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              Please check your inbox shortly.
            </p>
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {isOpen && (
          <>
            {/* BACKDROP */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            />

            {/* MODAL */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 60 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 60 }}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
              className="fixed inset-0 flex items-center justify-center z-50 px-4"
            >
              <div className="relative w-full max-w-md p-8 pt-14 rounded-2xl bg-white shadow-xl border">
                {/* CLOSE BUTTON */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-4 text-gray-500 hover:text-black text-2xl"
                >
                  ×
                </button>

                {/* HEADER (FORM STEP ONLY) */}
                {step === "form" && (
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-semibold text-gray-900">
                      Experience the AskLocal Client Journey
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">
                      You’ll start by seeing AskLocal exactly as your clients
                      do. Agents and partners can request internal access after
                      approval.
                    </p>
                  </div>
                )}

                {/* FORM STEP */}
                {step === "form" && (
                  <form onSubmit={handleFormSubmit} className="space-y-6">
                    {/* FIRST NAME */}
                    <div className="relative mt-2">
                      <User
                        className={`absolute left-3 top-3.5 w-5 h-5 ${
                          focused === "firstName"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />

                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        onFocus={() => setFocused("firstName")}
                        onBlur={() => setFocused(null)}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                        focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30 outline-none text-sm text-black/80 inter-medium transition-all"
                      />

                      <label
                        htmlFor="firstName"
                        className={`absolute left-10 px-1 bg-white pointer-events-none transition-all ${
                          focused === "firstName" || firstName
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        First Name
                      </label>
                    </div>

                    {/* LAST NAME */}
                    <div className="relative">
                      <User
                        className={`absolute left-3 top-3.5 w-5 h-5 ${
                          focused === "lastName"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />

                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        onFocus={() => setFocused("lastName")}
                        onBlur={() => setFocused(null)}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                        focus:border-[#4177BC] focus:ring-2 text-sm text-black/80 inter-medium  focus:ring-[#4177BC]/30 outline-none transition-all"
                      />

                      <label
                        htmlFor="lastName"
                        className={`absolute left-10 px-1 bg-white pointer-events-none transition-all ${
                          focused === "lastName" || lastName
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        Last Name
                      </label>
                    </div>

                    {/* MLS LICENSE */}
                    <div className="relative">
                      <BadgeCheck
                        className={`absolute left-3 top-3.5 w-5 h-5 ${
                          focused === "mlsLicense"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />

                      <input
                        id="mlsLicense"
                        type="text"
                        value={mlsLicense}
                        onChange={(e) => setMlsLicense(e.target.value)}
                        onFocus={() => setFocused("mlsLicense")}
                        onBlur={() => setFocused(null)}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                        focus:border-[#4177BC] focus:ring-2 text-sm text-black/80 inter-medium  focus:ring-[#4177BC]/30 outline-none transition-all"
                      />

                      <label
                        htmlFor="mlsLicense"
                        className={`absolute left-10 px-1 bg-white pointer-events-none transition-all ${
                          focused === "mlsLicense" || mlsLicense
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        MLS License
                      </label>
                    </div>

                    {/* EMAIL */}
                    <div className="relative">
                      <Mail
                        className={`absolute left-3 top-3.5 w-5 h-5 ${
                          focused === "email"
                            ? "text-[#4177BC]"
                            : "text-gray-400"
                        }`}
                      />

                      <input
                        id="emailAddress"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocused("email")}
                        onBlur={() => setFocused(null)}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg
                        focus:border-[#4177BC] focus:ring-2 text-sm text-black/80 inter-medium  focus:ring-[#4177BC]/30 outline-none transition-all"
                      />

                      <label
                        htmlFor="emailAddress"
                        className={`absolute left-10 px-1 bg-white pointer-events-none transition-all ${
                          focused === "email" || email
                            ? "-top-2 text-xs text-[#4177BC]"
                            : "top-3.5 text-sm text-gray-400"
                        }`}
                      >
                        Email Address
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white inter-semibold py-3 rounded-lg"
                    >
                      {loading ? "Processing..." : "continue"}
                    </button>
                    <p className="text-center text-xs -mt-4 inter-medium text-gray-500 ">
                      No commitment — preview only.
                    </p>
                  </form>
                )}

                {/* VERIFY STEP */}
                {step === "verify" && (
                  <div className="flex flex-col items-center gap-6">
                    <div className="text-center">
                      <CheckCircle2 className="mx-auto w-12 h-12 text-[#4177BC]" />
                      <h2 className="text-xl font-semibold mt-2">
                        Verify Your Email
                      </h2>
                      <p className="text-gray-600 text-sm mt-1">
                        Enter the 6-digit code sent to <b>{email}</b>.
                      </p>
                    </div>

                    <div className="flex justify-center gap-2">
                      {code.map((digit, i) => (
                        <input
                          key={i}
                          id={`code-${i}`}
                          maxLength={1}
                          type="text"
                          value={digit}
                          onChange={(e) => handleCodeChange(e.target.value, i)}
                          className="w-10 h-12 text-center text-lg border border-gray-300 
                          rounded-lg focus:border-[#4177BC] focus:ring-2 focus:ring-[#4177BC]/30  text-black/80 inter-medium  outline-none transition-all"
                        />
                      ))}
                    </div>

                    <button
                      onClick={handleVerify}
                      disabled={loading || code.some((c) => !c)}
                      className="w-full bg-linear-to-r from-[#4177BC] to-[#5EA8E8] text-white py-3 rounded-lg disabled:opacity-50"
                    >
                      {loading ? "Verifying..." : "Verify Email"}
                    </button>

                    <button
                      onClick={handleResend}
                      disabled={timer > 0}
                      className="text-sm text-[#4177BC]"
                    >
                      {timer > 0
                        ? `Resend code in ${timer}s`
                        : "Resend Verification Code"}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
